// Full site admin — edits everything live, persists to localStorage.
// Opens via floating gear (bottom-left) or Ctrl+Shift+A. Password: 'glamm'.
// All user-facing text is bilingual (TR/EN). Images can be URL or uploaded (base64).

import { useEffect, useRef, useState } from 'react';
import { ContentStore, useContent, TONE_BG, deepMerge } from './content.jsx';
import { api } from './api.js';

const ADMIN_AUTH_KEY = 'glamm:admin:auth';

/* =================== Trigger + Login =================== */

function AdminTrigger({ onOpen }) {
  return (
    <button onClick={onOpen} aria-label="Yönetim paneli"
      className="fixed bottom-6 left-6 z-[85] h-12 w-12 rounded-full bg-espresso text-cream shadow-[0_10px_30px_rgba(74,46,30,0.4)] hover:scale-105 transition grid place-items-center">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
      </svg>
    </button>
  );
}

function AdminLogin({ onAuth, onClose }) {
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (busy || !pw) return;
    setBusy(true);
    try {
      await api.login(pw);
      sessionStorage.setItem(ADMIN_AUTH_KEY, '1');
      onAuth();
    } catch (e) {
      if (e.status === 429) setErr('Çok fazla hatalı deneme — bir dakika bekle.');
      else if (e.status === 401) setErr('Hatalı şifre.');
      else setErr('Sunucuya ulaşılamadı. Backend çalışıyor mu?');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[95] grid place-items-center bg-ink/50 backdrop-blur-sm" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="w-[360px] bg-cream rounded-3xl p-7 shadow-[0_30px_80px_rgba(74,46,30,0.4)] border border-espresso/10">
        <div className="font-display text-[24px] text-espresso">Yönetim</div>
        <div className="text-[12px] text-espresso/65 mt-1">Siteyi düzenlemek için giriş yap.</div>
        <input type="password" autoFocus value={pw}
          onChange={e => { setPw(e.target.value); setErr(''); }}
          onKeyDown={e => e.key === 'Enter' && submit()}
          placeholder="Şifre"
          className="mt-5 w-full bg-white border border-espresso/15 rounded-xl px-4 py-2.5 outline-none focus:border-espresso/40 text-[14px] disabled:opacity-50"/>
        {err && <div className="mt-2 text-[12px] text-red-700">{err}</div>}
        <div className="mt-4 flex items-center gap-2">
          <button onClick={submit} disabled={busy}
            className="flex-1 rounded-full bg-espresso text-cream py-2.5 text-[13px] disabled:opacity-50">
            {busy ? '...' : 'Giriş'}
          </button>
          <button onClick={onClose} className="rounded-full border border-espresso/15 px-4 py-2.5 text-[13px] text-espresso/70">İptal</button>
        </div>
        <div className="mt-4 text-[10.5px] font-mono text-espresso/55 tracking-widest">
          Oturum, httpOnly cookie ile sunucuda tutulur.
        </div>
      </div>
    </div>
  );
}

/* =================== Panel =================== */

function AdminPanel({ onClose }) {
  const live = useContent();
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(live)));
  const [savedSnap, setSavedSnap] = useState(() => JSON.stringify(live));
  const [tab, setTab] = useState('visibility');
  const [toast, setToast] = useState(null);

  const dirty = JSON.stringify(draft) !== savedSnap;

  const notify = (msg) => { setToast(msg); setTimeout(() => setToast(null), 1800); };
  const patch = (part) => setDraft(prev => deepMerge(prev, part));

  const save = async () => {
    if (!dirty) return;
    const ok = await ContentStore.publish(draft);
    if (ok) {
      setSavedSnap(JSON.stringify(draft));
      notify('Kaydedildi — tüm ziyaretçiler artık yeni içeriği görüyor');
    } else {
      notify('Sunucuya kaydedilemedi — giriş süresi dolmuş olabilir');
    }
  };
  const revert = () => {
    if (!dirty) return;
    if (!confirm('Kaydedilmemiş değişiklikler geri alınsın mı?')) return;
    const fresh = ContentStore.get();
    setDraft(JSON.parse(JSON.stringify(fresh)));
    setSavedSnap(JSON.stringify(fresh));
  };

  // Warn before closing panel or navigating away with unsaved changes
  useEffect(() => {
    const onBeforeUnload = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const tryClose = () => {
    if (dirty && !confirm('Kaydedilmemiş değişiklikler var. Yine de kapatılsın mı?')) return;
    onClose();
  };

  // Ctrl+S shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dirty, draft]);

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(draft, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `glamm-content-${Date.now()}.json`;
    a.click(); URL.revokeObjectURL(url);
    notify('İçerik dışa aktarıldı');
  };
  const importJson = (file) => {
    const r = new FileReader();
    r.onload = () => {
      try {
        const parsed = JSON.parse(r.result);
        setDraft(parsed);
        notify('İçe aktarıldı — Kaydet\'e basmadan uygulanmaz');
      }
      catch { notify('Geçersiz JSON'); }
    };
    r.readAsText(file);
  };
  const reset = () => {
    if (confirm('Tüm içerik varsayılanlara sıfırlansın mı? (Kaydet\'e basana kadar uygulanmaz)')) {
      setDraft(ContentStore.defaults());
      notify('Varsayılanlara çekildi');
    }
  };
  const logout = async () => {
    if (dirty && !confirm('Kaydedilmemiş değişiklikler kaybolacak. Çıkılsın mı?')) return;
    try { await api.logout(); } catch {}
    sessionStorage.removeItem(ADMIN_AUTH_KEY);
    onClose();
  };

  // Use draft as the `c` passed to tabs
  const c = draft;

  const tabs = [
    { id: 'visibility', label: 'Görünürlük' },
    { id: 'brand',      label: 'Marka' },
    { id: 'seo',        label: 'SEO' },
    { id: 'hero',       label: 'Hero / 3D' },
    { id: 'experience', label: 'Deneyim' },
    { id: 'featured',   label: 'Öne Çıkan' },
    { id: 'campaigns',  label: 'Kampanyalar' },
    { id: 'menu',       label: 'Menü' },
    { id: 'branches',   label: 'Şubeler' },
    { id: 'stories',    label: 'Hikayeler' },
    { id: 'instagram',  label: 'Instagram' },
    { id: 'about',      label: 'Hakkımızda' },
    { id: 'contact',    label: 'İletişim' },
    { id: 'footer',     label: 'Footer' },
    { id: 'legal',      label: 'Yasal' },
    { id: 'security',   label: 'Güvenlik' },
    { id: 'data',       label: 'Veri' },
  ];

  return (
    <div className="fixed inset-0 z-[90] bg-cream overflow-y-auto overscroll-contain" data-lenis-prevent>
      <div className="min-h-full flex flex-col"
        style={{ animation: 'adminFadeIn .35s cubic-bezier(.2,.7,.2,1)' }}>
        <style>{`@keyframes adminFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }`}</style>
        <div className="sticky top-0 z-20 px-5 md:px-8 py-4 border-b border-espresso/10 flex items-center justify-between bg-cream/90 backdrop-blur">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-xl bg-espresso text-cream grid place-items-center font-display text-[14px]">G</div>
            <div>
              <div className="font-display text-[18px] text-espresso leading-none flex items-center gap-2">
                Glamm Yönetim
                {dirty && <span className="text-[10px] font-mono tracking-widest uppercase text-amber-700 bg-amber-100/80 px-1.5 py-0.5 rounded">· Değişiklik var</span>}
              </div>
              <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-tan mt-1">v2 · TR/EN · localStorage</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={revert} disabled={!dirty} title="Değişiklikleri geri al"
              className={`rounded-full border border-espresso/15 px-3 py-1.5 text-[12px] transition
                ${dirty ? 'text-espresso hover:bg-white/70' : 'text-espresso/30 cursor-not-allowed'}`}>
              Geri Al
            </button>
            <button onClick={save} disabled={!dirty} title="Kaydet (Ctrl+S)"
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[12.5px] transition shadow-sm
                ${dirty ? 'bg-espresso text-cream hover:bg-ink' : 'bg-espresso/15 text-espresso/40 cursor-not-allowed'}`}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8M7 3v5h8"/></svg>
              Kaydet
            </button>
            <div className="w-px h-6 bg-espresso/15 mx-1"/>
            <button onClick={logout} title="Çıkış"
              className="h-9 w-9 rounded-full border border-espresso/15 text-espresso/60 grid place-items-center hover:bg-white/70">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="M16 17l5-5-5-5"/><path d="M21 12H9"/></svg>
            </button>
            <button onClick={tryClose} title="Kapat"
              className="h-9 w-9 rounded-full border border-espresso/15 text-espresso/70 grid place-items-center hover:bg-white/70">✕</button>
          </div>
        </div>

        <div className="sticky top-[57px] md:top-[61px] z-10 px-3 md:px-6 py-2 border-b border-espresso/10 bg-cream/90 backdrop-blur">
          <TabStrip tabs={tabs} value={tab} onChange={setTab}/>
        </div>

        <div className="flex-1 mx-auto w-full max-w-5xl px-5 md:px-8 py-8 pb-28">
          {tab === 'visibility' && <TabVisibility c={c} patch={patch}/>}
          {tab === 'brand'      && <TabBrand      c={c} patch={patch}/>}
          {tab === 'seo'        && <TabSeo        c={c} patch={patch}/>}
          {tab === 'hero'       && <TabHero       c={c} patch={patch}/>}
          {tab === 'experience' && <TabExperience c={c} patch={patch}/>}
          {tab === 'featured'   && <TabFeatured   c={c} patch={patch}/>}
          {tab === 'campaigns'  && <TabCampaigns  c={c} patch={patch}/>}
          {tab === 'menu'       && <TabMenu       c={c} patch={patch}/>}
          {tab === 'branches'   && <TabBranches   c={c} patch={patch}/>}
          {tab === 'stories'    && <TabStories    c={c} patch={patch}/>}
          {tab === 'instagram'  && <TabInstagram  c={c} patch={patch}/>}
          {tab === 'about'      && <TabAbout      c={c} patch={patch}/>}
          {tab === 'contact'    && <TabContact    c={c} patch={patch}/>}
          {tab === 'footer'     && <TabFooter     c={c} patch={patch}/>}
          {tab === 'legal'      && <TabLegal      c={c} patch={patch}/>}
          {tab === 'security'   && <TabSecurity   notify={notify}/>}
          {tab === 'data'       && <TabData       exportJson={exportJson} importJson={importJson} reset={reset}/>}
        </div>

        {toast && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 bg-espresso text-cream text-[12.5px] px-5 py-2.5 rounded-full shadow-[0_10px_30px_rgba(74,46,30,0.4)]">
            {toast}
          </div>
        )}
      </div>
    </div>
  );
}

/* =================== Form primitives =================== */

function Row({ label, children, hint }) {
  return (
    <label className="block mb-3">
      {label && <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-tan mb-1.5">{label}</div>}
      {children}
      {hint && <div className="text-[10.5px] text-espresso/45 mt-1">{hint}</div>}
    </label>
  );
}
function TextIn({ value, onChange, placeholder, type = 'text' }) {
  return <input type={type} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
    className="w-full bg-white border border-espresso/15 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-espresso/40"/>;
}
function TextArea({ value, onChange, rows = 3 }) {
  return <textarea value={value ?? ''} onChange={e => onChange(e.target.value)} rows={rows}
    className="w-full bg-white border border-espresso/15 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-espresso/40 resize-y leading-relaxed"/>;
}
function NumberIn({ value, onChange, min, max, step = 1 }) {
  return <input type="number" value={value ?? 0} onChange={e => onChange(+e.target.value)} min={min} max={max} step={step}
    className="w-full bg-white border border-espresso/15 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-espresso/40"/>;
}

// Normalize a bilingual field (string → {tr,en} or object → object)
function asBi(v) {
  if (v == null) return { tr: '', en: '' };
  if (typeof v === 'string') return { tr: v, en: v };
  return { tr: v.tr ?? '', en: v.en ?? '' };
}

function BiTextIn({ value, onChange, placeholder, compact = false }) {
  const o = asBi(value);
  const cls = `flex-1 bg-white border border-espresso/15 rounded-lg px-3 py-${compact ? '1.5' : '2'} text-[13px] outline-none focus:border-espresso/40`;
  return (
    <div className="space-y-1.5">
      <div className="flex items-stretch gap-2">
        <span className="flex-none w-7 grid place-items-center font-mono text-[9px] tracking-widest text-tan bg-latte/40 rounded-lg">TR</span>
        <input value={o.tr} onChange={e => onChange({ tr: e.target.value, en: o.en })} placeholder={placeholder} className={cls}/>
      </div>
      <div className="flex items-stretch gap-2">
        <span className="flex-none w-7 grid place-items-center font-mono text-[9px] tracking-widest text-tan bg-latte/40 rounded-lg">EN</span>
        <input value={o.en} onChange={e => onChange({ tr: o.tr, en: e.target.value })} placeholder={placeholder} className={cls}/>
      </div>
    </div>
  );
}

function BiTextArea({ value, onChange, rows = 2 }) {
  const o = asBi(value);
  return (
    <div className="space-y-1.5">
      <div className="flex items-stretch gap-2">
        <span className="flex-none w-7 grid place-items-start pt-2 font-mono text-[9px] tracking-widest text-tan bg-latte/40 rounded-lg justify-center">TR</span>
        <textarea rows={rows} value={o.tr} onChange={e => onChange({ tr: e.target.value, en: o.en })}
          className="flex-1 bg-white border border-espresso/15 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-espresso/40 resize-y leading-relaxed"/>
      </div>
      <div className="flex items-stretch gap-2">
        <span className="flex-none w-7 grid place-items-start pt-2 font-mono text-[9px] tracking-widest text-tan bg-latte/40 rounded-lg justify-center">EN</span>
        <textarea rows={rows} value={o.en} onChange={e => onChange({ tr: o.tr, en: e.target.value })}
          className="flex-1 bg-white border border-espresso/15 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-espresso/40 resize-y leading-relaxed"/>
      </div>
    </div>
  );
}

function ImageIn({ value, onChange, hint = 'URL yapıştır veya dosya yükle (sunucuya)' }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const handleFile = async (file) => {
    setErr('');
    if (file.size > 8 * 1024 * 1024) {
      setErr('Görsel 8MB\'den büyük olamaz.');
      return;
    }
    if (!['image/jpeg','image/png','image/webp','image/gif'].includes(file.type)) {
      setErr('Sadece JPG, PNG, WebP, GIF desteklenir.');
      return;
    }
    setBusy(true);
    try {
      const res = await api.upload(file);
      onChange(res.url);
    } catch (e) {
      if (e.status === 401) setErr('Giriş süresi dolmuş — tekrar giriş yap.');
      else if (e.status === 413) setErr('Dosya çok büyük.');
      else setErr('Yükleme başarısız.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-start gap-3">
        <div className="h-16 w-16 rounded-xl flex-none overflow-hidden bg-white border border-espresso/15 relative">
          {value ? (
            <img src={value} className="h-full w-full object-cover" alt=""/>
          ) : (
            <div className="h-full w-full grid place-items-center text-espresso/30 text-[22px]">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="M21 15l-5-5L5 21"/></svg>
            </div>
          )}
          {busy && (
            <div className="absolute inset-0 bg-white/70 grid place-items-center text-[10px] font-mono text-espresso">...</div>
          )}
        </div>
        <div className="flex-1 space-y-2">
          <input value={value || ''} onChange={e => onChange(e.target.value)}
            placeholder="https://... veya /uploads/..."
            className="w-full bg-white border border-espresso/15 rounded-lg px-3 py-1.5 text-[11.5px] font-mono outline-none focus:border-espresso/40"/>
          <div className="flex items-center gap-2">
            <input ref={inputRef} type="file" accept="image/*" hidden
              onChange={e => e.target.files[0] && handleFile(e.target.files[0])}/>
            <button type="button" onClick={() => inputRef.current?.click()} disabled={busy}
              className="rounded-full bg-white border border-espresso/15 px-3 py-1 text-[11px] text-espresso hover:bg-latte/30 transition disabled:opacity-50">
              {busy ? 'Yükleniyor...' : 'Yükle'}
            </button>
            {value && (
              <button type="button" onClick={() => onChange('')}
                className="text-[11px] text-red-700/70 px-2">Kaldır</button>
            )}
          </div>
          {err && <div className="text-[11px] text-red-700">{err}</div>}
        </div>
      </div>
      {hint && <div className="text-[10.5px] text-espresso/45 mt-1">{hint}</div>}
    </div>
  );
}

function Toggle({ value, onChange, label }) {
  return (
    <button type="button" onClick={() => onChange(!value)}
      className={`flex items-center gap-2 rounded-full px-3 py-1.5 text-[12px] border transition
        ${value ? 'bg-espresso text-cream border-espresso' : 'bg-white border-espresso/15 text-espresso/70'}`}>
      <span className={`h-3 w-3 rounded-full ${value ? 'bg-tan' : 'bg-espresso/30'}`}/>
      {label}
    </button>
  );
}
function SelectIn({ value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      className="w-full bg-white border border-espresso/15 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-espresso/40">
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
function ColorIn({ value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={value} onChange={e => onChange(e.target.value)}
        className="h-9 w-14 rounded-lg border border-espresso/15"/>
      <input value={value} onChange={e => onChange(e.target.value)}
        className="flex-1 bg-white border border-espresso/15 rounded-lg px-3 py-2 text-[13px] font-mono outline-none focus:border-espresso/40"/>
    </div>
  );
}
function ToneSelect({ value, onChange }) {
  const tones = ['rose', 'cream', 'latte', 'cocoa', 'butter', 'pistachio', 'white'];
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {tones.map(t => (
        <button type="button" key={t} onClick={() => onChange(t)} title={t}
          className={`h-8 w-8 rounded-full border-2 transition ${value === t ? 'border-espresso scale-110' : 'border-white/80'}`}
          style={{ background: TONE_BG[t] }}/>
      ))}
    </div>
  );
}
function Card({ children, className = '' }) {
  return <div className={`rounded-2xl bg-white/70 border border-espresso/10 p-4 ${className}`}>{children}</div>;
}
function Section({ title, children, action }) {
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-[17px] text-espresso">{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}
function DelBtn({ onClick }) {
  return <button onClick={onClick} className="text-[11px] text-red-700/70 hover:text-red-800 px-2 py-0.5 rounded hover:bg-red-50 transition">Sil</button>;
}
function AddBtn({ onClick, label = '+ Ekle' }) {
  return <button onClick={onClick} className="rounded-full bg-espresso text-cream px-3 py-1 text-[11px] hover:bg-ink transition">{label}</button>;
}

function ItemRow({ thumb, title, subtitle, meta, expanded, onToggle, onDelete, accent, children }) {
  return (
    <div className={`bg-white border rounded-xl transition-all mb-2 ${expanded ? 'border-espresso/25 shadow-[0_8px_24px_rgba(74,46,30,0.08)]' : 'border-espresso/10 hover:border-espresso/20'}`}>
      <button type="button" onClick={onToggle}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-left">
        <div className="flex-none h-10 w-10 rounded-lg overflow-hidden bg-latte/40 grid place-items-center ring-1 ring-espresso/10"
          style={accent ? { background: accent } : undefined}>
          {thumb ? (
            <img src={thumb} alt="" className="h-full w-full object-cover"/>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#a17a4f" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] text-espresso font-medium truncate">
            {title || <span className="text-espresso/40 italic font-normal">— isimsiz —</span>}
          </div>
          {subtitle && <div className="text-[11.5px] text-espresso/55 truncate mt-0.5">{subtitle}</div>}
        </div>
        {meta && <div className="flex-none text-[11px] text-espresso/60 font-mono whitespace-nowrap ml-2">{meta}</div>}
        {onDelete && (
          <span role="button" tabIndex={0}
            onClick={(e) => { e.stopPropagation(); if (confirm('Silinsin mi?')) onDelete(); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); if (confirm('Silinsin mi?')) onDelete(); } }}
            className="flex-none h-7 w-7 ml-1 rounded-full grid place-items-center text-espresso/45 hover:text-red-700 hover:bg-red-50 transition cursor-pointer">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
          </span>
        )}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          className={`flex-none text-espresso/40 transition-transform ml-1 ${expanded ? 'rotate-180' : ''}`}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {expanded && <div className="px-4 pb-4 pt-2 border-t border-espresso/10">{children}</div>}
    </div>
  );
}

function useExpanded() {
  const [id, setId] = useState(null);
  return { id, toggle: (k) => setId(prev => prev === k ? null : k) };
}

function SearchIn({ value, onChange, placeholder = 'Ara...' }) {
  return (
    <div className="relative flex-1">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        className="absolute left-3 top-1/2 -translate-y-1/2 text-espresso/40">
        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-white border border-espresso/15 rounded-full pl-9 pr-8 py-2 text-[13px] outline-none focus:border-espresso/40"/>
      {value && (
        <button onClick={() => onChange('')} type="button"
          className="absolute right-2.5 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full text-espresso/50 hover:bg-latte/40 grid place-items-center text-[11px]">×</button>
      )}
    </div>
  );
}

/* =================== Tabs =================== */

function HScroll({ children, className = '', itemSelector, activeKey }) {
  const ref = useRef(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const update = () => {
    const el = ref.current; if (!el) return;
    setEdges({
      left: el.scrollLeft > 4,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
    });
  };

  useEffect(() => {
    update();
    const el = ref.current; if (!el) return;
    const onScroll = () => update();
    const onResize = () => update();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => { el.removeEventListener('scroll', onScroll); window.removeEventListener('resize', onResize); };
  }, []);

  useEffect(() => {
    if (!itemSelector || activeKey == null) return;
    const el = ref.current; if (!el) return;
    const btn = el.querySelector(itemSelector.replace('{key}', String(activeKey).replace(/"/g, '\\"')));
    if (btn && btn.scrollIntoView) btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeKey, itemSelector]);

  const onWheel = (e) => {
    const el = ref.current; if (!el) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      el.scrollLeft += e.deltaY;
      if (el.scrollWidth > el.clientWidth) e.preventDefault();
    }
  };

  const nudge = (dir) => {
    const el = ref.current; if (!el) return;
    el.scrollBy({ left: dir * 220, behavior: 'smooth' });
  };

  return (
    <div className="relative">
      {edges.left && (
        <button type="button" onClick={() => nudge(-1)} aria-label="Sola"
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-7 w-7 rounded-full bg-cream border border-espresso/15 grid place-items-center shadow-sm hover:bg-white">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
      )}
      {edges.right && (
        <button type="button" onClick={() => nudge(1)} aria-label="Sağa"
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-7 w-7 rounded-full bg-cream border border-espresso/15 grid place-items-center shadow-sm hover:bg-white">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      )}
      <div ref={ref} onWheel={onWheel}
        className={`overflow-x-auto no-scrollbar flex gap-1 scroll-smooth ${className}`}
        style={{ scrollSnapType: 'x proximity' }}>
        {children}
      </div>
    </div>
  );
}

function TabStrip({ tabs, value, onChange }) {
  return (
    <div className="mx-auto max-w-6xl">
      <HScroll className="px-8" itemSelector={`[data-tab="{key}"]`} activeKey={value}>
        {tabs.map(t => (
          <button key={t.id} data-tab={t.id} onClick={() => onChange(t.id)}
            style={{ scrollSnapAlign: 'center' }}
            className={`flex-none px-3.5 py-1.5 rounded-full text-[12.5px] whitespace-nowrap transition
              ${value === t.id ? 'bg-espresso text-cream' : 'text-espresso/70 hover:bg-white/70 border border-espresso/10'}`}>
            {t.label}
          </button>
        ))}
      </HScroll>
    </div>
  );
}

function SectionToggle({ on, onChange, label, hint }) {
  return (
    <label className="flex items-start justify-between gap-4 py-3 cursor-pointer select-none">
      <div className="min-w-0">
        <div className="text-[13.5px] text-espresso font-medium">{label}</div>
        {hint && <div className="text-[11.5px] text-espresso/55 mt-0.5">{hint}</div>}
      </div>
      <button type="button" onClick={() => onChange(!on)} role="switch" aria-checked={on}
        className={`relative shrink-0 mt-0.5 h-6 w-11 rounded-full transition-colors ${on ? 'bg-espresso' : 'bg-espresso/20'}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${on ? 'left-[22px]' : 'left-0.5'}`}/>
      </button>
    </label>
  );
}

function TabVisibility({ c, patch }) {
  const v = c.visibility || {};
  const set = (k, val) => patch({ visibility: { [k]: val } });
  const items = [
    { key: 'hero',       label: 'Hero — Üst bölüm / kahve fincanı',   hint: 'Sayfanın ilk ekranı (başlık, CTA, 3D fincan ve buhar).' },
    { key: 'experience', label: 'Deneyim',                             hint: 'Kahve ritüeli / deneyim bölümü.' },
    { key: 'featured',   label: 'Öne Çıkanlar — "Bu hafta masada"',    hint: 'Haftanın seçkileri kartları.' },
    { key: 'campaigns',  label: 'Kampanyalar',                         hint: 'Büyük kampanya bannerı ve yan kartlar.' },
    { key: 'branches',   label: 'Şubeler',                             hint: 'Şube listesi ve Türkiye haritası.' },
    { key: 'instagram',  label: 'Instagram Akışı',                     hint: 'Sahte Instagram post grid\'i.' },
    { key: 'about',      label: 'Hakkımızda',                          hint: '"Hikaye" bölümü.' },
    { key: 'contact',    label: 'İletişim',                            hint: 'Form ve iletişim bilgileri.' },
    { key: 'footer',     label: 'Footer',                              hint: 'En alt bölüm (logo, linkler, telif, yasal).' },
  ];
  const allOn = items.every(it => v[it.key] !== false);
  const toggleAll = () => {
    const next = {};
    items.forEach(it => { next[it.key] = !allOn; });
    patch({ visibility: next });
  };
  return (
    <Section title="Bölüm Görünürlüğü"
      action={
        <button onClick={toggleAll}
          className="rounded-full border border-espresso/15 text-espresso/70 px-3 py-1 text-[11px] hover:bg-white/70 transition">
          {allOn ? 'Tümünü Gizle' : 'Tümünü Göster'}
        </button>
      }>
      <Card>
        <div className="text-[11.5px] text-espresso/55 mb-1">
          Aç/kapa anahtarları — değişiklik anında sitede uygulanır. Gizlenen bölüm ziyaretçiye görünmez.
        </div>
        <div className="divide-y divide-espresso/10">
          {items.map(it => (
            <SectionToggle key={it.key} on={v[it.key] !== false} onChange={val => set(it.key, val)}
              label={it.label} hint={it.hint}/>
          ))}
        </div>
      </Card>
    </Section>
  );
}

function TabBrand({ c, patch }) {
  const b = c.brand;
  return (
    <Section title="Marka Bilgileri">
      <Card>
        <Row label="Marka Adı"><BiTextIn value={b.name} onChange={v => patch({ brand: { name: v } })}/></Row>
        <Row label="Alt Yazı"><BiTextIn value={b.tagline} onChange={v => patch({ brand: { tagline: v } })}/></Row>
        <Row label="Şehir"><BiTextIn value={b.city} onChange={v => patch({ brand: { city: v } })}/></Row>
        <Row label="Sezon Etiketi"><BiTextIn value={b.seasonLabel} onChange={v => patch({ brand: { seasonLabel: v } })}/></Row>
        <div className="grid grid-cols-2 gap-3">
          <Row label="Instagram"><TextIn value={b.instagram} onChange={v => patch({ brand: { instagram: v } })}/></Row>
          <Row label="Telefon"><TextIn value={b.phone} onChange={v => patch({ brand: { phone: v } })}/></Row>
        </div>
        <Row label="E-posta"><TextIn value={b.email} onChange={v => patch({ brand: { email: v } })}/></Row>
        <div className="grid grid-cols-2 gap-3">
          <Row label="Ana Renk"><ColorIn value={b.primaryColor} onChange={v => patch({ brand: { primaryColor: v } })}/></Row>
          <Row label="Aksan"><ColorIn value={b.accent} onChange={v => patch({ brand: { accent: v } })}/></Row>
        </div>
        <Row label="Logo" hint="Header, splash, 3D kupa ve menü sayfasında görünen logo">
          <ImageIn value={b.logo} onChange={v => patch({ brand: { logo: v } })}/>
        </Row>
        <Row label="Favicon" hint="Tarayıcı sekmesinde görünen ikon (PNG/ICO önerilir, kare şeklinde)">
          <ImageIn value={b.favicon} onChange={v => patch({ brand: { favicon: v } })}/>
        </Row>
      </Card>
    </Section>
  );
}

function TabSeo({ c, patch }) {
  const s = c.seo || {};
  return (
    <>
      <Section title="Arama Motoru (SEO)">
        <Card>
          <Row label="Site Başlığı" hint="Tarayıcı sekmesinde ve Google arama sonuçlarında görünür (60 karakterden kısa olması önerilir)">
            <BiTextIn value={s.title} onChange={v => patch({ seo: { title: v } })}/>
          </Row>
          <Row label="Meta Açıklama" hint="Google arama sonuçlarında başlığın altında görünür (155 karakterden kısa)">
            <BiTextArea rows={2} value={s.description} onChange={v => patch({ seo: { description: v } })}/>
          </Row>
          <Row label="Anahtar Kelimeler" hint="Virgülle ayrılmış (arama motorları için öneri)">
            <BiTextIn value={s.keywords} onChange={v => patch({ seo: { keywords: v } })}/>
          </Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Varsayılan Dil" hint="HTML lang attr — 'tr' veya 'en'">
              <TextIn value={s.lang} onChange={v => patch({ seo: { lang: v } })}/>
            </Row>
            <Row label="Tema Rengi" hint="Mobil tarayıcı çubuğu rengi">
              <ColorIn value={s.themeColor} onChange={v => patch({ seo: { themeColor: v } })}/>
            </Row>
          </div>
          <Row label="Kanonik URL" hint="Asıl adres (opsiyonel) — örn. https://glamm.coffee">
            <TextIn value={s.canonical} onChange={v => patch({ seo: { canonical: v } })}/>
          </Row>
        </Card>
      </Section>

      <Section title="Sosyal Paylaşım (Open Graph / Twitter)">
        <Card>
          <Row label="Paylaşım Başlığı" hint="Facebook, WhatsApp, Twitter'da paylaşıldığında görünür">
            <BiTextIn value={s.ogTitle} onChange={v => patch({ seo: { ogTitle: v } })}/>
          </Row>
          <Row label="Paylaşım Açıklaması">
            <BiTextArea rows={2} value={s.ogDescription} onChange={v => patch({ seo: { ogDescription: v } })}/>
          </Row>
          <Row label="Paylaşım Görseli" hint="1200×630 px önerilir — JPG/PNG">
            <ImageIn value={s.ogImage} onChange={v => patch({ seo: { ogImage: v } })}/>
          </Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Site URL'si" hint="Opsiyonel — boş bırakılırsa mevcut adres kullanılır">
              <TextIn value={s.ogUrl} onChange={v => patch({ seo: { ogUrl: v } })}/>
            </Row>
            <Row label="Twitter Kart Tipi" hint="summary veya summary_large_image">
              <TextIn value={s.twitterCard} onChange={v => patch({ seo: { twitterCard: v } })}/>
            </Row>
          </div>
        </Card>
      </Section>
    </>
  );
}

function TabHero({ c, patch }) {
  const h = c.hero;
  return (
    <>
      <Section title="Hero Metinleri">
        <Card>
          <Row label="Başlık" hint="Satır atlamak için Enter kullan"><BiTextArea rows={3} value={h.title} onChange={v => patch({ hero: { title: v } })}/></Row>
          <Row label="Alt Başlık"><BiTextArea rows={2} value={h.sub} onChange={v => patch({ hero: { sub: v } })}/></Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="CTA Birincil"><BiTextIn value={h.ctaPrimary} onChange={v => patch({ hero: { ctaPrimary: v } })}/></Row>
            <Row label="CTA İkincil"><BiTextIn value={h.ctaSecondary} onChange={v => patch({ hero: { ctaSecondary: v } })}/></Row>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Yıldız Puanı"><TextIn value={h.rating} onChange={v => patch({ hero: { rating: v } })}/></Row>
            <Row label="Puan Altı"><BiTextIn value={h.ratingLabel} onChange={v => patch({ hero: { ratingLabel: v } })}/></Row>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Row label="İkinci Stat"><TextIn value={h.stat} onChange={v => patch({ hero: { stat: v } })}/></Row>
            <Row label="Stat Altı"><BiTextIn value={h.statLabel} onChange={v => patch({ hero: { statLabel: v } })}/></Row>
          </div>
        </Card>
      </Section>
      <Section title="3D Kupa & Efektler">
        <Card>
          <div className="flex flex-wrap gap-2 mb-3">
            <Toggle value={h.showSteam}     onChange={v => patch({ hero: { showSteam: v } })}     label="Buhar"/>
            <Toggle value={h.showBeans}     onChange={v => patch({ hero: { showBeans: v } })}     label="Taneler"/>
            <Toggle value={h.showLogoOnCup} onChange={v => patch({ hero: { showLogoOnCup: v } })} label="Logo"/>
          </div>
          <Row label={`Kupa dönüş hızı · ${h.cupSpeed}s`}>
            <input type="range" min="6" max="40" value={h.cupSpeed}
              onChange={e => patch({ hero: { cupSpeed: +e.target.value } })}
              className="w-full accent-espresso"/>
          </Row>
          <Row label={`Tane sayısı · ${h.beanCount}`}>
            <input type="range" min="0" max="40" value={h.beanCount}
              onChange={e => patch({ hero: { beanCount: +e.target.value } })}
              className="w-full accent-espresso"/>
          </Row>
          <Row label="Kupa Stili">
            <SelectIn value={h.cupStyle} onChange={v => patch({ hero: { cupStyle: v } })}
              options={[
                { value: 'porcelain', label: 'Porselen (parlak)' },
                { value: 'ceramic',   label: 'Seramik (yumuşak)' },
                { value: 'matte',     label: 'Mat' },
              ]}/>
          </Row>
        </Card>
      </Section>

      <Section title="Kupa Çevresi Çipleri">
        <ChipPicker label="Üst çip (Signature / Americano)" menu={c.menu.items}
          cfg={h.signatureChip || { show: true, menuId: null, labelOverride: { tr: '', en: '' } }}
          onChange={v => patch({ hero: { signatureChip: v } })}
          defaultLabelHint="Boş = ürünün rozeti (Signature/New...) ya da 'Signature' yazısı"/>
        <ChipPicker label="Alt çip (Taze / Granola Bowl)" menu={c.menu.items}
          cfg={h.freshChip || { show: true, menuId: null, labelOverride: { tr: '', en: '' } }}
          onChange={v => patch({ hero: { freshChip: v } })}
          defaultLabelHint="Boş = 'Taze / Fresh now'"/>
      </Section>
    </>
  );
}

function ChipPicker({ label, menu, cfg, onChange, defaultLabelHint }) {
  const opts = [
    { value: '', label: '— Otomatik seç —' },
    ...menu.map(m => ({
      value: String(m.id),
      label: `${(m.name?.tr || m.name?.en || '#' + m.id)}${m.tag ? ` · ${m.tag}` : ''}`,
    })),
  ];
  return (
    <Card className="mb-3">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-tan">{label}</div>
        <SectionToggle on={cfg.show !== false}
          onChange={v => onChange({ ...cfg, show: v })}
          label=""/>
      </div>
      {cfg.show !== false && (
        <>
          <Row label="Ürün" hint="Otomatik seçimde Signature rozeti olan ilk ürün gelir.">
            <SelectIn value={cfg.menuId == null ? '' : String(cfg.menuId)}
              onChange={v => onChange({ ...cfg, menuId: v === '' ? null : Number(v) })}
              options={opts}/>
          </Row>
          <Row label="Üst Etiket (opsiyonel)" hint={defaultLabelHint}>
            <BiTextIn value={cfg.labelOverride} onChange={v => onChange({ ...cfg, labelOverride: v })}/>
          </Row>
        </>
      )}
    </Card>
  );
}

function TabExperience({ c, patch }) {
  const list = c.experience;
  const exp = useExpanded();
  const upd = (i, k, v) => patch({ experience: list.map((it, idx) => idx === i ? { ...it, [k]: v } : it) });
  const add = () => { patch({ experience: [...list, { k: String(list.length + 1).padStart(2, '0'), t: { tr: 'Yeni', en: 'New' }, d: { tr: '', en: '' } }] }); exp.toggle(list.length); };
  const del = (i) => patch({ experience: list.filter((_, idx) => idx !== i) });
  return (
    <Section title={`Deneyim Kartları · ${list.length}`} action={<AddBtn onClick={add}/>}>
      {list.map((it, i) => (
        <ItemRow key={i}
          title={it.t?.tr || it.t?.en}
          subtitle={it.d?.tr}
          meta={`№ ${it.k}`}
          expanded={exp.id === i}
          onToggle={() => exp.toggle(i)}
          onDelete={() => del(i)}>
          <div className="grid grid-cols-4 gap-3 items-start">
            <Row label="Numara"><TextIn value={it.k} onChange={v => upd(i, 'k', v)}/></Row>
            <div className="col-span-3"><Row label="Başlık"><BiTextIn value={it.t} onChange={v => upd(i, 't', v)}/></Row></div>
          </div>
          <Row label="Açıklama"><BiTextArea rows={2} value={it.d} onChange={v => upd(i, 'd', v)}/></Row>
        </ItemRow>
      ))}
    </Section>
  );
}

function TabFeatured({ c, patch }) {
  const list = c.featured;
  const fe = useExpanded();
  const upd = (i, k, v) => patch({ featured: list.map((x, j) => j === i ? { ...x, [k]: v } : x) });
  const add = () => {
    patch({ featured: [...list, { menuId: null, name: { tr: '', en: '' }, tone: '', image: '', label: { tr: '', en: '' } }] });
    fe.toggle(list.length);
  };
  const del = (i) => patch({ featured: list.filter((_, j) => j !== i) });

  const menuOpts = [
    { value: '', label: '— Özel (menüden bağımsız) —' },
    ...c.menu.items.map(m => ({
      value: String(m.id),
      label: `${(m.name?.tr || m.name?.en || '#' + m.id)}${m.tag ? ` · ${m.tag}` : ''}`,
    })),
  ];

  return (
    <Section title={`Öne Çıkanlar · ${list.length}`} action={<AddBtn onClick={add}/>}>
      <div className="mb-3 text-[11.5px] text-espresso/60 leading-relaxed">
        Menüdeki bir ürünü seç — kartta o ürün gösterilir (görsel/isim/ton otomatik). Alternatif olarak kendi
        özel kartını da oluşturabilirsin (ürün seçme, görsel/isim/etiket alanlarını doldur).
      </div>
      {list.map((it, i) => {
        const linked = it.menuId != null ? c.menu.items.find(m => m.id === it.menuId) : null;
        const previewName = (it.name?.tr || it.name?.en) || (linked && (linked.name?.tr || linked.name?.en)) || '';
        const previewThumb = it.image || linked?.image;
        const previewTone = it.tone || linked?.tone;
        return (
          <ItemRow key={i}
            thumb={previewThumb}
            accent={!previewThumb ? TONE_BG[previewTone] : undefined}
            title={previewName}
            subtitle={linked ? '🔗 Menü ürünü' : 'Özel kart'}
            expanded={fe.id === i}
            onToggle={() => fe.toggle(i)}
            onDelete={() => del(i)}>
            <Row label="Menüden Ürün" hint="Bir ürün seçersen kart o ürünün görseli/ismiyle dolar. Aşağıdaki alanları bırakırsan menüden devralır.">
              <SelectIn value={it.menuId == null ? '' : String(it.menuId)}
                onChange={v => upd(i, 'menuId', v === '' ? null : Number(v))}
                options={menuOpts}/>
            </Row>
            <div className="mt-2 mb-3 text-[11px] font-mono tracking-[0.2em] uppercase text-tan">Kart görünümü (opsiyonel override)</div>
            <Row label="Görsel" hint={linked ? 'Boş bırakırsan menü ürününün görseli kullanılır.' : null}>
              <ImageIn value={it.image} onChange={v => upd(i, 'image', v)}/>
            </Row>
            <Row label="İsim" hint={linked ? 'Boş bırakırsan menü ürününün ismi kullanılır.' : null}>
              <BiTextIn value={it.name} onChange={v => upd(i, 'name', v)}/>
            </Row>
            <Row label="Etiket (görsel üzerinde)" hint={linked ? 'Boş bırakırsan ürünün rozeti (Signature/New...) kullanılır.' : null}>
              <BiTextIn value={it.label} onChange={v => upd(i, 'label', v)}/>
            </Row>
            <Row label="Ton" hint={linked ? 'Boş bırakırsan menü ürününün tonu kullanılır.' : null}>
              <ToneSelect value={it.tone} onChange={v => upd(i, 'tone', v)}/>
            </Row>
          </ItemRow>
        );
      })}
    </Section>
  );
}

function TabCampaigns({ c, patch }) {
  const h = c.campaigns.hero;
  const sides = c.campaigns.side;
  const side = useExpanded();
  const upH = (k, v) => patch({ campaigns: { hero: { [k]: v } } });
  const upS = (i, k, v) => {
    const next = sides.map((x, j) => j === i ? { ...x, [k]: v } : x);
    patch({ campaigns: { side: next } });
  };
  const addSide = () => {
    const next = [...sides, { label: { tr: 'Yeni', en: 'New' }, title: { tr: 'Başlık', en: 'Title' }, note: { tr: '', en: '' }, tone: 'cream', link: '' }];
    patch({ campaigns: { side: next } });
    side.toggle(next.length - 1);
  };
  const delSide = (i) => patch({ campaigns: { side: sides.filter((_, j) => j !== i) } });

  return (
    <>
      <Section title="Ana Kampanya">
        <Card>
          <div className="mb-3 text-[11.5px] text-espresso/60 leading-relaxed">
            Butonlar için <b>Link</b> alanlarını doldur. Örnek değerler:
            <code className="mx-1 px-1.5 py-0.5 rounded bg-latte/40 font-mono text-[10.5px]">#/tam-menu</code> (Tam menü),
            <code className="mx-1 px-1.5 py-0.5 rounded bg-latte/40 font-mono text-[10.5px]">#stores</code> (şubelere kaydır),
            <code className="mx-1 px-1.5 py-0.5 rounded bg-latte/40 font-mono text-[10.5px]">#contact</code> (iletişim),
            veya <code className="mx-1 px-1.5 py-0.5 rounded bg-latte/40 font-mono text-[10.5px]">https://...</code> (yeni sekmede açılır).
            Boş bırakırsan buton tıklanamaz olur.
          </div>
          <Row label="Üst Etiket"><BiTextIn value={h.label} onChange={v => upH('label', v)}/></Row>
          <Row label="Başlık"><BiTextIn value={h.title} onChange={v => upH('title', v)}/></Row>
          <Row label="Metin"><BiTextArea rows={3} value={h.body} onChange={v => upH('body', v)}/></Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="CTA 1 (birincil)"><BiTextIn value={h.cta1} onChange={v => upH('cta1', v)}/></Row>
            <Row label="CTA 1 Link"><TextIn value={h.cta1Link} onChange={v => upH('cta1Link', v)} placeholder="#/tam-menu veya https://..."/></Row>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Row label="CTA 2 (ikincil)"><BiTextIn value={h.cta2} onChange={v => upH('cta2', v)}/></Row>
            <Row label="CTA 2 Link"><TextIn value={h.cta2Link} onChange={v => upH('cta2Link', v)} placeholder="#stores veya https://..."/></Row>
          </div>
        </Card>
      </Section>

      <Section title={`Yan Kartlar · ${sides.length}`} action={<AddBtn onClick={addSide} label="+ Yan Kart"/>}>
        {sides.length === 0 && <div className="text-[12px] text-espresso/50 italic mb-3">Henüz yan kart yok.</div>}
        {sides.map((s, i) => (
          <ItemRow key={i}
            accent={TONE_BG[s.tone] || TONE_BG.cream}
            title={s.title?.tr || s.title?.en || 'İsimsiz'}
            subtitle={s.label?.tr || s.label?.en}
            meta={s.link ? '🔗' : null}
            expanded={side.id === i}
            onToggle={() => side.toggle(i)}
            onDelete={() => delSide(i)}>
            <div className="grid grid-cols-2 gap-3">
              <Row label="Üst Etiket"><BiTextIn value={s.label} onChange={v => upS(i, 'label', v)}/></Row>
              <Row label="Ton"><ToneSelect value={s.tone} onChange={v => upS(i, 'tone', v)}/></Row>
            </div>
            <Row label="Başlık"><BiTextIn value={s.title} onChange={v => upS(i, 'title', v)}/></Row>
            <Row label="Not"><BiTextIn value={s.note} onChange={v => upS(i, 'note', v)}/></Row>
            <Row label="Link" hint="Tıklanınca yönlendirilecek yer. Örn: #/tam-menu, #stores, https://...">
              <TextIn value={s.link} onChange={v => upS(i, 'link', v)} placeholder="#/tam-menu"/>
            </Row>
          </ItemRow>
        ))}
      </Section>
    </>
  );
}

function TabMenu({ c, patch }) {
  const items = c.menu.items;
  const cats = c.menu.categories;
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const item = useExpanded();
  const cat = useExpanded();

  const catById = (id) => cats.find(x => x.id === id);
  const view = items.filter(x => {
    if (filter !== 'all' && x.cat !== filter) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return [x.name?.tr, x.name?.en, x.tag, x.price].some(v => String(v || '').toLowerCase().includes(s));
  });

  const upd = (id, k, v) => patch({ menu: { items: items.map(x => x.id === id ? { ...x, [k]: v } : x) } });
  const add = () => {
    const id = Math.max(0, ...items.map(x => x.id)) + 1;
    patch({ menu: { items: [...items, { id, cat: filter === 'all' ? (cats.find(c => c.id !== 'all')?.id || 'sicak') : filter, name: { tr: 'Yeni Ürün', en: 'New Item' }, price: '₺0', tag: '', note: { tr: '', en: '' }, tone: 'cream', image: '' }] } });
    item.toggle(id);
  };
  const del = (id) => patch({ menu: { items: items.filter(x => x.id !== id) } });

  const updCat = (id, k, v) => patch({ menu: { categories: cats.map(x => x.id === id ? { ...x, [k]: v } : x) } });
  const addCat = () => {
    const newId = 'cat_' + Date.now().toString(36);
    patch({ menu: { categories: [...cats, { id: newId, label: { tr: 'Yeni Kategori', en: 'New Category' }, image: '', tone: 'cream' }] } });
    cat.toggle(newId);
  };
  const delCat = (id) => {
    if (id === 'all') return;
    if (items.some(it => it.cat === id)) {
      if (!confirm('Bu kategoride ürünler var. Yine de silinsin mi? Ürünler de silinir.')) return;
      patch({ menu: { items: items.filter(it => it.cat !== id), categories: cats.filter(x => x.id !== id) } });
    } else {
      patch({ menu: { categories: cats.filter(x => x.id !== id) } });
    }
  };

  const realCats = cats.filter(x => x.id !== 'all');

  return (
    <>
      <Section title={`Kategoriler · ${realCats.length}`} action={<AddBtn onClick={addCat} label="+ Kategori"/>}>
        {realCats.length === 0 && <div className="text-[12px] text-espresso/50 italic mb-3">Henüz kategori yok.</div>}
        {realCats.map(k => (
          <ItemRow key={k.id}
            thumb={k.image}
            accent={!k.image ? TONE_BG[k.tone || 'cream'] : undefined}
            title={k.label?.tr || k.id}
            subtitle={k.label?.en}
            meta={`${items.filter(it => it.cat === k.id).length} ürün`}
            expanded={cat.id === k.id}
            onToggle={() => cat.toggle(k.id)}
            onDelete={() => delCat(k.id)}>
            <Row label="Kategori Görseli"><ImageIn value={k.image} onChange={v => updCat(k.id, 'image', v)}/></Row>
            <Row label="İsim"><BiTextIn value={k.label} onChange={v => updCat(k.id, 'label', v)}/></Row>
            <Row label="Arka Plan Tonu"><ToneSelect value={k.tone || 'cream'} onChange={v => updCat(k.id, 'tone', v)}/></Row>
          </ItemRow>
        ))}
      </Section>

      <Section title={`Menü · ${items.length} ürün${q || filter !== 'all' ? ` · ${view.length} sonuç` : ''}`}
        action={<AddBtn onClick={add} label="+ Ürün"/>}>
        <div className="flex items-center gap-2 mb-3">
          <SearchIn value={q} onChange={setQ} placeholder="İsim, rozet, fiyat ara..."/>
        </div>
        <div className="pb-2 mb-3">
          <HScroll className="px-8" itemSelector={`[data-cat="{key}"]`} activeKey={filter}>
            {cats.map(x => (
              <button key={x.id} data-cat={x.id} onClick={() => setFilter(x.id)}
                style={{ scrollSnapAlign: 'center' }}
                className={`flex-none rounded-full px-3 py-1.5 text-[11.5px] border transition
                  ${filter === x.id ? 'bg-espresso text-cream border-espresso' : 'bg-white border-espresso/15 text-espresso/70 hover:bg-latte/30'}`}>
                {(x.label && (x.label.tr || x.label.en)) || x.id}
                {x.id !== 'all' && <span className="ml-1.5 font-mono text-[10px] opacity-60">{items.filter(it => it.cat === x.id).length}</span>}
              </button>
            ))}
          </HScroll>
        </div>

        {view.length === 0 && (
          <div className="text-[12.5px] text-espresso/50 italic text-center py-8">
            {q ? 'Arama sonucu yok.' : 'Bu kategoride ürün yok.'}
          </div>
        )}

        {view.map(it => {
          const catLabel = catById(it.cat)?.label?.tr || it.cat;
          return (
            <ItemRow key={it.id}
              thumb={it.image}
              accent={!it.image ? TONE_BG[it.tone || 'cream'] : undefined}
              title={it.name?.tr || it.name?.en}
              subtitle={`${catLabel}${it.tag ? ' · ' + it.tag : ''}`}
              meta={it.price}
              expanded={item.id === it.id}
              onToggle={() => item.toggle(it.id)}
              onDelete={() => del(it.id)}>
              <Row label="Görsel"><ImageIn value={it.image} onChange={v => upd(it.id, 'image', v)}/></Row>
              <Row label="İsim"><BiTextIn value={it.name} onChange={v => upd(it.id, 'name', v)}/></Row>
              <Row label="Not / Tarif"><BiTextArea rows={2} value={it.note} onChange={v => upd(it.id, 'note', v)}/></Row>
              <div className="grid grid-cols-2 gap-3">
                <Row label="Fiyat"><TextIn value={it.price} onChange={v => upd(it.id, 'price', v)}/></Row>
                <Row label="Kategori">
                  <SelectIn value={it.cat} onChange={v => upd(it.id, 'cat', v)}
                    options={realCats.map(c => ({ value: c.id, label: c.label?.tr || c.id }))}/>
                </Row>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Row label="Rozet"><TextIn value={it.tag} onChange={v => upd(it.id, 'tag', v)} placeholder="Signature, New, Bestseller..."/></Row>
                <Row label="Ton"><ToneSelect value={it.tone} onChange={v => upd(it.id, 'tone', v)}/></Row>
              </div>
            </ItemRow>
          );
        })}
      </Section>
    </>
  );
}

function TabBranches({ c, patch }) {
  const list = c.branches;
  const br = useExpanded();
  const upd = (id, k, v) => patch({ branches: list.map(x => x.id === id ? { ...x, [k]: v } : x) });
  const add = () => {
    const id = Math.max(0, ...list.map(x => x.id)) + 1;
    patch({ branches: [...list, { id, name: { tr: 'Yeni Şube', en: 'New Store' }, addr: { tr: '', en: '' }, hours: '09:00 – 23:00', tag: '', x: 55, y: 60, phone: '', mapLink: '' }] });
    br.toggle(id);
  };
  const del = (id) => patch({ branches: list.filter(x => x.id !== id) });
  return (
    <Section title={`Şubeler · ${list.length}`} action={<AddBtn onClick={add} label="+ Şube"/>}>
      {list.map(b => (
        <ItemRow key={b.id}
          title={b.name?.tr || b.name?.en}
          subtitle={b.addr?.tr || b.tag}
          meta={b.hours}
          expanded={br.id === b.id}
          onToggle={() => br.toggle(b.id)}
          onDelete={() => del(b.id)}>
          <Row label="İsim"><BiTextIn value={b.name} onChange={v => upd(b.id, 'name', v)}/></Row>
          <Row label="Rozet"><BiTextIn value={b.tag} onChange={v => upd(b.id, 'tag', v)}/></Row>
          <Row label="Adres"><BiTextArea rows={2} value={b.addr} onChange={v => upd(b.id, 'addr', v)}/></Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Çalışma Saatleri"><TextIn value={b.hours} onChange={v => upd(b.id, 'hours', v)}/></Row>
            <Row label="Telefon"><TextIn value={b.phone} onChange={v => upd(b.id, 'phone', v)}/></Row>
          </div>
          <Row label="Harita URL"><TextIn value={b.mapLink} onChange={v => upd(b.id, 'mapLink', v)}/></Row>
          <div className="grid grid-cols-2 gap-3">
            <Row label={`Harita X · ${b.x}%`}>
              <input type="range" min="0" max="100" step="0.1" value={b.x} onChange={e => upd(b.id, 'x', +e.target.value)} className="w-full accent-espresso"/>
            </Row>
            <Row label={`Harita Y · ${b.y}%`}>
              <input type="range" min="0" max="100" step="0.1" value={b.y} onChange={e => upd(b.id, 'y', +e.target.value)} className="w-full accent-espresso"/>
            </Row>
          </div>
          <div className="text-[10.5px] text-espresso/50">İpucu: Adana çevresi ~x 55, y 60</div>
        </ItemRow>
      ))}
    </Section>
  );
}

function TabStories({ c, patch }) {
  const list = c.stories;
  const st = useExpanded();
  const upd = (id, k, v) => patch({ stories: list.map(x => x.id === id ? { ...x, [k]: v } : x) });
  const add = () => {
    const id = Math.max(0, ...list.map(x => x.id)) + 1;
    patch({ stories: [...list, { id, title: { tr: 'Yeni', en: 'New' }, image: '', tone: 'rose' }] });
    st.toggle(id);
  };
  const del = (id) => patch({ stories: list.filter(x => x.id !== id) });
  return (
    <Section title={`Story Halkaları · ${list.length}`} action={<AddBtn onClick={add} label="+ Story"/>}>
      {list.map(s => (
        <ItemRow key={s.id}
          thumb={s.image}
          accent={!s.image ? TONE_BG[s.tone] : undefined}
          title={s.title?.tr || s.title?.en}
          expanded={st.id === s.id}
          onToggle={() => st.toggle(s.id)}
          onDelete={() => del(s.id)}>
          <Row label="Görsel"><ImageIn value={s.image} onChange={v => upd(s.id, 'image', v)}/></Row>
          <Row label="Başlık"><BiTextIn value={s.title} onChange={v => upd(s.id, 'title', v)}/></Row>
          <Row label="Ton"><ToneSelect value={s.tone} onChange={v => upd(s.id, 'tone', v)}/></Row>
        </ItemRow>
      ))}
    </Section>
  );
}

function TabInstagram({ c, patch }) {
  const ig = c.instagram;
  const posts = ig.posts;
  const upIg = (k, v) => patch({ instagram: { [k]: v } });
  const upP = (id, k, v) => patch({ instagram: { posts: posts.map(x => x.id === id ? { ...x, [k]: v } : x) } });
  const add = () => {
    const id = Math.max(0, ...posts.map(x => x.id)) + 1;
    patch({ instagram: { posts: [...posts, { id, tone: 'rose', image: '', postUrl: ig.url || '', caption: { tr: '', en: '' }, likes: 0, comments: 0, date: '', tag: '' }] } });
  };
  const del = (id) => patch({ instagram: { posts: posts.filter(x => x.id !== id) } });
  const ps = useExpanded();
  return (
    <>
      <Section title="Instagram Hesabı">
        <Card>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Kullanıcı Adı"><TextIn value={ig.handle} onChange={v => upIg('handle', v)}/></Row>
            <Row label="Takipçi"><TextIn value={ig.followers} onChange={v => upIg('followers', v)}/></Row>
          </div>
          <Row label="Profil URL"><TextIn value={ig.url} onChange={v => upIg('url', v)}/></Row>
          <Row label="Bio"><BiTextArea rows={2} value={ig.bio} onChange={v => upIg('bio', v)}/></Row>
        </Card>
      </Section>
      <Section title={`Gönderiler · ${posts.length}`} action={<AddBtn onClick={() => { add(); }} label="+ Post"/>}>
        <div className="mb-3 p-3 rounded-xl bg-white/60 border border-dashed border-espresso/20 text-[11.5px] text-espresso/70 leading-relaxed">
          Gerçek gönderilerini eklemek için: Instagram gönderisini aç → sağ üst "..." → "Bağlantıyı kopyala".
          Görsel için gönderinin ekran görüntüsünü "Görsel" alanına yükle. "Post URL" alanına da linki yapıştır.
        </div>
        {posts.map(p => (
          <ItemRow key={p.id}
            thumb={p.image}
            accent={!p.image ? TONE_BG[p.tone] : undefined}
            title={p.caption?.tr || p.caption?.en || `Post #${p.id}`}
            subtitle={p.tag || p.postUrl}
            meta={p.likes ? `♥ ${p.likes}` : p.date}
            expanded={ps.id === p.id}
            onToggle={() => ps.toggle(p.id)}
            onDelete={() => del(p.id)}>
            <Row label="Görsel"><ImageIn value={p.image} onChange={v => upP(p.id, 'image', v)}/></Row>
            <Row label="Post URL (Instagram)"><TextIn value={p.postUrl} onChange={v => upP(p.id, 'postUrl', v)} placeholder="https://www.instagram.com/p/..."/></Row>
            <Row label="Açıklama"><BiTextArea rows={2} value={p.caption} onChange={v => upP(p.id, 'caption', v)}/></Row>
            <div className="grid grid-cols-3 gap-3">
              <Row label="Beğeni"><NumberIn value={p.likes} onChange={v => upP(p.id, 'likes', v)}/></Row>
              <Row label="Yorum"><NumberIn value={p.comments} onChange={v => upP(p.id, 'comments', v)}/></Row>
              <Row label="Zaman"><TextIn value={p.date} onChange={v => upP(p.id, 'date', v)} placeholder="2g · 1h"/></Row>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Row label="Etiket"><TextIn value={p.tag} onChange={v => upP(p.id, 'tag', v)}/></Row>
              <Row label="Ton (yedek)"><ToneSelect value={p.tone} onChange={v => upP(p.id, 'tone', v)}/></Row>
            </div>
          </ItemRow>
        ))}
      </Section>
    </>
  );
}

function TabAbout({ c, patch }) {
  const a = c.about;
  const upStat = (i, j, v) => {
    const next = a.stats.map((s, idx) => {
      if (idx !== i) return s;
      const copy = s.slice(); copy[j] = v; return copy;
    });
    patch({ about: { stats: next } });
  };
  return (
    <Section title="Hakkımızda">
      <Card>
        <Row label="Üst Yazı"><BiTextIn value={a.kicker} onChange={v => patch({ about: { kicker: v } })}/></Row>
        <Row label="Başlık"><BiTextArea rows={2} value={a.title} onChange={v => patch({ about: { title: v } })}/></Row>
        <Row label="Metin"><BiTextArea rows={4} value={a.body} onChange={v => patch({ about: { body: v } })}/></Row>
        <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-tan mb-2 mt-3">İstatistikler</div>
        {a.stats.map((s, i) => (
          <div key={i} className="grid grid-cols-2 gap-2 mb-3 p-2 rounded-lg bg-white border border-espresso/10">
            <BiTextIn value={s[0]} onChange={v => upStat(i, 0, v)} compact/>
            <BiTextIn value={s[1]} onChange={v => upStat(i, 1, v)} compact/>
          </div>
        ))}
      </Card>
    </Section>
  );
}

function TabContact({ c, patch }) {
  const ct = c.contact;
  return (
    <Section title="İletişim">
      <Card>
        <Row label="Üst Yazı"><BiTextIn value={ct.kicker} onChange={v => patch({ contact: { kicker: v } })}/></Row>
        <Row label="Başlık"><BiTextIn value={ct.title} onChange={v => patch({ contact: { title: v } })}/></Row>
        <Row label="Açıklama"><BiTextArea rows={3} value={ct.body} onChange={v => patch({ contact: { body: v } })}/></Row>
        <div className="text-[11px] text-espresso/55 mt-1">İletişim e-postası, telefon, instagram — "Marka" sekmesinden.</div>
      </Card>
    </Section>
  );
}

function TabFooter({ c, patch }) {
  const f = c.footer;

  const upCol = (i, k, v) => {
    const next = f.cols.map((x, j) => j === i ? { ...x, [k]: v } : x);
    patch({ footer: { cols: next } });
  };
  const addCol = () => {
    const next = [...f.cols, { title: { tr: 'Yeni Sütun', en: 'New Column' }, items: [] }];
    patch({ footer: { cols: next } });
  };
  const delCol = (i) => {
    if (!confirm('Bu sütun silinsin mi?')) return;
    patch({ footer: { cols: f.cols.filter((_, j) => j !== i) } });
  };
  // Normalize legacy shape on read; always write new shape { label, link }
  const normItem = (raw) => {
    if (raw && typeof raw === 'object' && 'label' in raw) {
      return { label: raw.label || { tr: '', en: '' }, link: raw.link || '' };
    }
    return { label: raw || { tr: '', en: '' }, link: '' };
  };
  const addItem = (i) => {
    const next = f.cols.map((col, ci) => ci === i
      ? { ...col, items: [...col.items, { label: { tr: 'Yeni Bağlantı', en: 'New Link' }, link: '' }] }
      : col);
    patch({ footer: { cols: next } });
  };
  const upColItemField = (i, j, field, v) => {
    const next = f.cols.map((col, ci) => {
      if (ci !== i) return col;
      const items = col.items.slice();
      const cur = normItem(items[j]);
      items[j] = { ...cur, [field]: v };
      return { ...col, items };
    });
    patch({ footer: { cols: next } });
  };
  const delColItem = (i, j) => {
    const next = f.cols.map((col, ci) => {
      if (ci !== i) return col;
      return { ...col, items: col.items.filter((_, k) => k !== j) };
    });
    patch({ footer: { cols: next } });
  };

  return (
    <>
      <Section title="Footer Metinleri">
        <Card>
          <Row label="Açıklama"><BiTextArea rows={2} value={f.blurb} onChange={v => patch({ footer: { blurb: v } })}/></Row>
          <Row label="Copyright"><BiTextIn value={f.copyright} onChange={v => patch({ footer: { copyright: v } })}/></Row>
          <Row label="Bülten Başlığı"><BiTextIn value={f.newsletter.title} onChange={v => patch({ footer: { newsletter: { title: v } } })}/></Row>
          <Row label="Bülten Alt"><BiTextIn value={f.newsletter.sub} onChange={v => patch({ footer: { newsletter: { sub: v } } })}/></Row>
        </Card>
      </Section>

      <Section title={`Bağlantı Sütunları · ${f.cols.length}`} action={<AddBtn onClick={addCol} label="+ Sütun"/>}>
        {f.cols.map((col, i) => (
          <Card key={i} className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-tan">Sütun #{i+1}</div>
              <DelBtn onClick={() => delCol(i)}/>
            </div>
            <Row label="Başlık"><BiTextIn value={col.title} onChange={v => upCol(i, 'title', v)}/></Row>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-tan mb-2 mt-4">Bağlantılar · {col.items.length}</div>
            <div className="text-[11px] text-espresso/55 mb-3 leading-relaxed">
              Her bağlantının iki parçası var: <b>yazı</b> (TR/EN) ve <b>link</b>. Örnek link değerleri:
              <code className="mx-1 px-1.5 py-0.5 rounded bg-latte/40 font-mono text-[10.5px]">#/tam-menu</code>
              <code className="mx-1 px-1.5 py-0.5 rounded bg-latte/40 font-mono text-[10.5px]">#stores</code>
              <code className="mx-1 px-1.5 py-0.5 rounded bg-latte/40 font-mono text-[10.5px]">#about</code>
              <code className="mx-1 px-1.5 py-0.5 rounded bg-latte/40 font-mono text-[10.5px]">#contact</code>
              <code className="mx-1 px-1.5 py-0.5 rounded bg-latte/40 font-mono text-[10.5px]">https://...</code>
            </div>
            {col.items.length === 0 && <div className="text-[11.5px] text-espresso/50 italic mb-2">Henüz bağlantı yok.</div>}
            {col.items.map((raw, j) => {
              const it = normItem(raw);
              return (
                <div key={j} className="p-3 mb-2 rounded-xl bg-white border border-espresso/10">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 space-y-2">
                      <BiTextIn value={it.label} onChange={v => upColItemField(i, j, 'label', v)} compact placeholder="Yazı (TR/EN)"/>
                      <TextIn value={it.link} onChange={v => upColItemField(i, j, 'link', v)} placeholder="#/tam-menu veya https://..."/>
                    </div>
                    <button onClick={() => delColItem(i, j)} title="Sil"
                      className="flex-none h-7 w-7 rounded-full grid place-items-center text-espresso/50 hover:text-red-700 hover:bg-red-50 transition">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/></svg>
                    </button>
                  </div>
                </div>
              );
            })}
            <button onClick={() => addItem(i)}
              className="mt-2 rounded-full border border-espresso/15 text-espresso/70 px-3 py-1 text-[11px] hover:bg-white/70 transition">
              + Bağlantı
            </button>
          </Card>
        ))}
      </Section>
    </>
  );
}

function TabLegal({ c, patch }) {
  const legal = c.legal || {};
  const updPage = (key, field, v) => patch({ legal: { [key]: { [field]: v } } });
  const pages = [
    { key: 'privacy', heading: 'Gizlilik Politikası', link: '#/gizlilik' },
    { key: 'kvkk',    heading: 'KVKK Aydınlatma',    link: '#/kvkk' },
    { key: 'cookies', heading: 'Çerez Politikası',   link: '#/cerezler' },
  ];
  return (
    <>
      <Section title="Son Güncelleme">
        <Card>
          <Row label="Tarih (YYYY-MM-DD)" hint="Sayfa başlığının altında görünür.">
            <TextIn value={legal.updated} onChange={v => patch({ legal: { updated: v } })}/>
          </Row>
        </Card>
      </Section>

      {pages.map(p => (
        <Section key={p.key} title={p.heading}
          action={<a href={p.link} target="_blank" rel="noreferrer"
            className="rounded-full border border-espresso/15 text-espresso/70 px-3 py-1 text-[11px] hover:bg-white/70 transition">
            Önizle ↗
          </a>}>
          <Card>
            <Row label="Başlık"><BiTextIn value={legal[p.key]?.title} onChange={v => updPage(p.key, 'title', v)}/></Row>
            <Row label="İçerik" hint='"## Başlık" satır başı alt başlık; "• " madde işareti olarak render edilir.'>
              <BiTextArea rows={14} value={legal[p.key]?.body} onChange={v => updPage(p.key, 'body', v)}/>
            </Row>
          </Card>
        </Section>
      ))}
    </>
  );
}

function TabSecurity({ notify }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const strength = (() => {
    const s = next;
    if (!s) return null;
    let score = 0;
    if (s.length >= 8) score++;
    if (s.length >= 12) score++;
    if (/[A-Z]/.test(s) && /[a-z]/.test(s)) score++;
    if (/\d/.test(s)) score++;
    if (/[^A-Za-z0-9]/.test(s)) score++;
    return ['Çok zayıf', 'Zayıf', 'Orta', 'İyi', 'Güçlü', 'Çok güçlü'][Math.min(score, 5)];
  })();

  const change = async () => {
    setErr('');
    if (!current || !next) return setErr('Tüm alanları doldur.');
    if (next.length < 6) return setErr('Yeni şifre en az 6 karakter olmalı.');
    if (next !== confirm) return setErr('Yeni şifreler eşleşmiyor.');
    setBusy(true);
    try {
      await api.changePassword(current, next);
      setCurrent(''); setNext(''); setConfirm('');
      notify('Şifre güncellendi');
    } catch (e) {
      if (e.status === 401) setErr('Mevcut şifre hatalı.');
      else if (e.status === 400) setErr('Geçersiz istek.');
      else setErr('Sunucuya ulaşılamadı.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Section title="Şifre">
        <Card>
          <Row label="Mevcut Şifre">
            <input type="password" value={current} onChange={e => setCurrent(e.target.value)}
              className="w-full bg-white border border-espresso/15 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-espresso/40"/>
          </Row>
          <Row label="Yeni Şifre" hint="En az 6 karakter. Harf + rakam + sembol karışımı önerilir.">
            <input type="password" value={next} onChange={e => setNext(e.target.value)}
              className="w-full bg-white border border-espresso/15 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-espresso/40"/>
          </Row>
          {strength && (
            <div className="mb-3 -mt-1 text-[11px] font-mono text-espresso/60">
              Güç: <b className="text-espresso">{strength}</b>
            </div>
          )}
          <Row label="Yeni Şifre (Tekrar)">
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && change()}
              className="w-full bg-white border border-espresso/15 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-espresso/40"/>
          </Row>
          {err && <div className="text-[12px] text-red-700 mb-2">{err}</div>}
          <button onClick={change} disabled={busy}
            className="rounded-full bg-espresso text-cream px-5 py-2 text-[13px] hover:bg-ink transition disabled:opacity-50">
            {busy ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}
          </button>
          <div className="mt-4 text-[11px] text-espresso/55 leading-relaxed">
            Şifre sunucuda bcrypt ile hash'lenmiş halde saklanır. Şifreyi unutursan sunucuda
            <code className="font-mono"> scripts/install.sh</code> çalıştırıp "Admin şifresini değiştir" seçeneğiyle sıfırlayabilirsin.
          </div>
        </Card>
      </Section>

      <Section title="Oturum">
        <Card>
          <div className="text-[12.5px] text-espresso/75 mb-3">
            Giriş, sunucu tarafında httpOnly JWT cookie ile 12 saat tutulur.
            Rate limit: dakikada 8 hatalı deneme.
          </div>
          <button onClick={async () => { try { await api.logout(); } catch {} sessionStorage.removeItem(ADMIN_AUTH_KEY); notify('Çıkış yapıldı'); location.reload(); }}
            className="rounded-full border border-espresso/15 px-4 py-2 text-[12.5px] text-espresso hover:bg-white/70 transition">
            Çıkış yap
          </button>
        </Card>
      </Section>
    </>
  );
}

function TabData({ exportJson, importJson, reset }) {
  const fileRef = useRef(null);
  return (
    <Section title="Veri Yönetimi">
      <Card>
        <p className="text-[13px] text-espresso/75 leading-relaxed">
          Tüm içerik tarayıcında <b className="font-mono">localStorage</b> altında saklanıyor.
          Dışa aktar → JSON indir. İçe aktar → JSON yükle. Sıfırla → varsayılanlara dön.
        </p>
        <div className="flex flex-wrap gap-2 mt-4">
          <button onClick={exportJson} className="rounded-full bg-espresso text-cream px-4 py-2 text-[12.5px]">↓ Dışa Aktar</button>
          <input ref={fileRef} type="file" accept="application/json" hidden
            onChange={e => e.target.files[0] && importJson(e.target.files[0])}/>
          <button onClick={() => fileRef.current?.click()} className="rounded-full border border-espresso/20 px-4 py-2 text-[12.5px] text-espresso">↑ İçe Aktar</button>
          <button onClick={reset} className="rounded-full border border-red-700/30 text-red-700 px-4 py-2 text-[12.5px] hover:bg-red-50">Sıfırla</button>
        </div>
      </Card>
      <div className="mt-4 text-[11px] text-espresso/55">
        Not: görseller base64 olarak saklanıyor — çok sayıda büyük görsel localStorage sınırını (5-10MB) aşabilir.
        Uzun vadede görselleri CDN / S3 / Cloudinary'e yükleyip URL vermek daha doğru.
      </div>
    </Section>
  );
}

/* =================== Root =================== */

export function AdminRoot() {
  const [open, setOpen] = useState(false);
  const [authed, setAuthed] = useState(() => sessionStorage.getItem(ADMIN_AUTH_KEY) === '1');

  useEffect(() => {
    const check = () => {
      const h = (window.location.hash || '').toLowerCase();
      if (h === '#admin' || h === '#/admin') { setOpen(true); return; }
      const params = new URLSearchParams(window.location.search);
      if (params.get('admin') === '1') setOpen(true);
    };
    check();
    const onHash = () => check();
    const onKey = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === 'A' || e.key === 'a')) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('hashchange', onHash);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  // When panel opens, verify with server that the cookie is still valid.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const s = await api.authStatus();
        if (!s.authed && authed) {
          sessionStorage.removeItem(ADMIN_AUTH_KEY);
          setAuthed(false);
        }
      } catch { /* backend unreachable — let login form handle it */ }
    })();
  }, [open]);

  return (
    <>
      {open && !authed && <AdminLogin onAuth={() => setAuthed(true)} onClose={() => setOpen(false)}/>}
      {open && authed && <AdminPanel onClose={() => setOpen(false)}/>}
    </>
  );
}
